<script setup lang="ts">
/**
 * UI-kit component
 * Category: Inputs
 * Name: 'AppPhoneInput'
 *
 * Phone entry built on `vue-tel-input` with a `maska` national-format mask.
 * The model is the validated E.164 number (empty while the typed number is
 * incomplete or invalid).
 */
import { useId } from 'vue'
import { VueTelInput } from 'vue-tel-input'

import type { AppPhoneInputProps } from '../types/interfaces'

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppIcon } from '@shared/ui/icons'

import {
  APP_PHONE_IGNORED_COUNTRIES,
  APP_PHONE_PREFERRED_COUNTRIES,
} from '../types/enums'
import { useAppPhoneInput } from '../composables/app-phone-input.hook'

defineOptions({
  name: 'AppPhoneInput',
})

const props = withDefaults(defineProps<AppPhoneInputProps>(), {
  state: undefined,
  label: undefined,
  required: false,
  placeholder: '',
  message: undefined,
  name: undefined,
  disabled: false,
  defaultCountry: undefined,
  invalidMsg: 'Please enter a valid phone number',
})

const emit = defineEmits<{
  'update:valid': [valid: boolean]
}>()

const inputId = useId()
const messageId = useId()

const model = defineModel<string>({
  default: '',
})

const {
  national,
  initialCountry,
  inputOptions,
  dropdownOptions,
  resolvedState,
  resolvedMessage,
  changeCountry,
} = useAppPhoneInput({
  model,
  props,
  inputId,
  messageId,
  onValid: (valid: boolean) => emit('update:valid', valid),
})
</script>

<template>
  <div
    class="app-phone-input"
    :class="{
      'app-phone-input--error': resolvedState === 'error',
      'app-phone-input--success': resolvedState === 'success',
      'app-phone-input--disabled': disabled,
    }"
  >
    <label
      v-if="label"
      class="app-phone-input__label"
      :for="inputId"
    >
      {{ label }}<span
        v-if="required"
        class="app-phone-input__required"
      >*</span>
    </label>

    <VueTelInput
      v-model="national"
      mode="national"
      :default-country="initialCountry"
      :auto-default-country="false"
      :ignored-countries="APP_PHONE_IGNORED_COUNTRIES"
      :preferred-countries="APP_PHONE_PREFERRED_COUNTRIES"
      :input-options="inputOptions"
      :dropdown-options="dropdownOptions"
      :valid-characters-only="true"
      :auto-format="false"
      :disabled="disabled"
      @country-changed="changeCountry"
    >
      <template #arrow-icon>
        <AppIcon
          class="app-phone-input__chevron"
          :icon="faChevronDown"
          size="small"
          color="slate-500"
        />
      </template>
    </VueTelInput>

    <AppText
      v-if="resolvedMessage"
      :id="messageId"
      name="body2"
      :color="resolvedState === 'error' ? 'red-600' : 'slate-500'"
      class="app-phone-input__message"
    >
      {{ resolvedMessage }}
    </AppText>
  </div>
</template>

<style lang="scss" scoped src="../styles/_app-phone-input.scss" />
